import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { AcademicAssessmentsBoxs, type AcademicAssessment } from './';
import {
  useGetAcademicQualificationAssessment,
  useCreateAcademicQualificationAssessment,
  type FilterType,
  useCreateDsrcAssessment,
} from '../../../../graphql';
import { PAGE_TEXT } from '../../../../lang';
import {
  Accordion,
  Loading,
  Alert,
  type AlertDetails,
} from '../../../../../../components';

type AcademicAssessmentsProps = {
  academicQualificationIds: string[];
  ifRecommendForHKPFS?: boolean;
  onJumpToNext?: () => void;
  refetchAssessment?: () => void;
  isViewMode?: boolean;
};

export const AcademicAssessments: React.FunctionComponent<
  AcademicAssessmentsProps
> = props => {
  const { applicationId = '' } = useParams();
  const {
    academicQualificationIds,
    isViewMode = false,
    ifRecommendForHKPFS = false,
    refetchAssessment = () => {},
    onJumpToNext = () => {},
  } = props;
  const { DRC_ASSESSMENT_DETAILS } = PAGE_TEXT;
  const STEP_TEXT = DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_A;

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  let initialValues: AcademicAssessment[] = academicQualificationIds.map(
    (aqId, index) => ({
      title: `${STEP_TEXT.TITLE} - ${index + 1}`,
      convertedGPA: -1.0,
      academicQualificationReferenceId: aqId,
      selectWorldUniversityRanking: false,
      selectNationalRanking: false,
      selectWorldSubjectRanking: false,
      selectDisciplineSpecificList: false,
      notApplicable: false,
    }),
  );

  const filter: FilterType = {
    applicationId,
  };
  const {
    academicQualificationAssessment,
    loading: academicAssessmentLoading,
    error: academicAssessmentError,
    refetch: academicAssessmentRefetch,
  } = useGetAcademicQualificationAssessment(filter);

  if (
    !academicAssessmentLoading &&
    academicQualificationAssessment.length > 0
  ) {
    // merge the history
    const initialValuesWithHistory: AcademicAssessment[] = initialValues.map(
      aq => {
        const { academicQualificationReferenceId, title: initTitle } = aq;
        const assessedAQ = academicQualificationAssessment.find(
          aq =>
            aq.academicQualificationReferenceId ===
            academicQualificationReferenceId,
        );

        if (assessedAQ !== undefined) {
          const { title, ...others } = assessedAQ;
          return {
            title: initTitle,
            ...others,
            academicQualificationReferenceId, // to overwrite
          };
        } else
          return {
            title: initTitle,
            academicQualificationReferenceId,
            selectWorldUniversityRanking: false,
            selectNationalRanking: false,
            selectWorldSubjectRanking: false,
            selectDisciplineSpecificList: false,
            notApplicable: false,
          };
      },
    );
    initialValues = [...initialValuesWithHistory];
  }

  const createEmptyAcademicQualificationAssessment =
    useCreateAcademicQualificationAssessment();

  // initialize the Academic Assessments in backend
  // according to the number & order of  Academic in application
  const handleInitialBackend = async (): Promise<void> => {
    try {
      const variables = {
        input: {
          applicationId,
          academicQualificationAssessmentInputs: initialValues,
        },
      };
      await createEmptyAcademicQualificationAssessment({
        variables,
      });
      academicAssessmentRefetch();
    } catch (error: any) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (error as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const createDsrcAssessment = useCreateDsrcAssessment();
  const handleDsrcAssessmentCreation = async (): Promise<void> => {
    try {
      await createDsrcAssessment({
        variables: {
          applicationId,
        },
      });
    } catch (e) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (e as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  useEffect(() => {
    const initializeAssessments = async (): Promise<void> => {
      if (
        !academicAssessmentLoading &&
        academicQualificationAssessment.length !==
          academicQualificationIds.length
      ) {
        await handleInitialBackend();
        await handleDsrcAssessmentCreation();
      }
    };

    // call the async function
    initializeAssessments().catch(e => {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (e as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    });
  }, [academicAssessmentLoading]);

  if (academicAssessmentError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` + (academicAssessmentError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const loading =
    academicAssessmentLoading ||
    academicQualificationAssessment?.length !==
      academicQualificationIds?.length;

  return (
    <ContentBox>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />

      {loading ? (
        <Loading />
      ) : (
        <Accordion
          className="w-full"
          defaultExpanded
          title={STEP_TEXT.TITLE}
          details={
            <Stack padding={2}>
              {academicQualificationIds.length > 0 ? (
                <Box>
                  {academicQualificationIds.map(
                    (academicQualificationId, index) => {
                      const curAqAssessment =
                        academicQualificationAssessment?.find(
                          ({ academicQualificationReferenceId }) =>
                            academicQualificationReferenceId ===
                            academicQualificationId,
                        );
                      return curAqAssessment === undefined ? (
                        <Loading />
                      ) : (
                        <AcademicAssessmentsBoxs
                          key={index}
                          index={index + 1}
                          degreeTitle={`${STEP_TEXT.TITLE} - ${index + 1}`}
                          academicQualificationId={academicQualificationId}
                          initialAssessmentValue={curAqAssessment}
                          allValuesArr={academicQualificationAssessment}
                          ifRecommendForHKPFS={ifRecommendForHKPFS}
                          isViewMode={isViewMode}
                          refetchAssessment={refetchAssessment}
                          //  only show Next button in the last degree
                          onJumpToNext={
                            index === academicQualificationIds.length - 1
                              ? onJumpToNext
                              : undefined
                          }
                        />
                      );
                    },
                  )}
                </Box>
              ) : (
                <div className="empty-text">
                  {DRC_ASSESSMENT_DETAILS.EMPTY_TEXT.ACADEMIC_QUALIFICATION}
                </div>
              )}
            </Stack>
          }
        />
      )}
    </ContentBox>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .empty-text {
    display: flex;
    justify-content: center;
    margin-top: 3rem;
    margin-bottom: 3rem;
  }
`;
